import type { Schedule } from '@/__generated__/openapi-types';
import { useCurrentOrganization } from '@/components/features/membership/membership-provider';
import { ScheduleNotFoundCard } from '@/components/features/schedule/schedule-not-found-card';
import { SchedulePastille } from '@/components/features/schedule/schedule-pastille';
import { ScheduleSelector } from '@/components/features/schedule/schedule-selector';
import {
  Page,
  PageContent,
  PageHeader,
  PageHeaderAction,
  PageHeaderDescription,
  PageHeaderInfo,
  PageHeaderTitle,
} from '@/components/ui/layout/page';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { capitalize } from '@/lib/utils';
import { getOrganizationSchedulesQuery } from '@/queries/getOrganizationSchedulesQuery';
import { getScheduleQuery } from '@/queries/getScheduleQuery';
import { useQueries } from '@tanstack/react-query';
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { BarChart3, InfoIcon, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/schedule/$scheduleId')({
  component: RouteComponent,
  loader: async ({ params, location }) => {
    const getActiveTab = ():
      | 'details'
      | 'automations'
      | 'analytics'
      | undefined => {
      const lastMatch = location.pathname;

      if (lastMatch.includes('details')) {
        return 'details';
      }

      if (lastMatch.includes('automations')) {
        return 'automations';
      }

      if (lastMatch.includes('analytics')) {
        return 'analytics';
      }

      return undefined;
    };

    const activeTab = getActiveTab();

    // TODO: Remove this when we have a better way to do it
    if (!activeTab && !location.pathname.includes('edit')) {
      throw redirect({
        to: '/schedule/$scheduleId/details',
        params: { scheduleId: params.scheduleId },
      });
    }

    return { activeTab };
  },
});

function RouteComponent() {
  const { scheduleId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeTab } = Route.useLoaderData();

  const { organizationId } = useCurrentOrganization();

  const [{ data: schedules }, { data: schedule, isFetched }] = useQueries({
    queries: [
      getOrganizationSchedulesQuery(organizationId),
      getScheduleQuery(organizationId, scheduleId),
    ],
  });

  const handleOnScheduleChange = (schedule: Schedule) => {
    navigate({
      to: '/schedule/$scheduleId/details',
      params: { scheduleId: schedule.scheduleId },
    });
  };

  if (!isFetched) {
    return null;
  }

  if (!schedule) {
    return <ScheduleNotFoundCard />;
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderInfo>
          <div className="flex items-center gap-2">
            <SchedulePastille color={schedule.color} size="lg" variant="line" />
            <PageHeaderTitle>{capitalize(schedule.name)}</PageHeaderTitle>
          </div>
          <PageHeaderDescription>
            {schedule.description ||
              t('authenticated.schedule.details.noDescription')}
          </PageHeaderDescription>
        </PageHeaderInfo>

        <PageHeaderAction>
          <ScheduleSelector
            schedules={schedules}
            selectedSchedule={schedule}
            onScheduleChange={handleOnScheduleChange}
          />
        </PageHeaderAction>
      </PageHeader>

      {activeTab && (
        <PageContent>
          <Tabs defaultValue={activeTab}>
            <TabsList className="mb-4 w-full sm:w-fit max-w-full sm:max-w-none overflow-x-auto">
              <TabsTrigger
                value="details"
                className="px-3 sm:px-6 flex items-center group min-w-0 flex-1 sm:flex-none"
                onClick={() =>
                  navigate({
                    to: '/schedule/$scheduleId/details',
                    params: { scheduleId },
                  })
                }
              >
                <InfoIcon className="h-4 w-4 sm:h-5 sm:w-5 group-data-[state=active]:text-blue-500 flex-shrink-0" />
                <span className="truncate">
                  {t('authenticated.schedule.details.tabs.details')}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="automations"
                className="px-3 sm:px-6 flex items-center group min-w-0 flex-1 sm:flex-none"
                onClick={() =>
                  navigate({
                    to: '/schedule/$scheduleId/automations',
                    params: { scheduleId },
                  })
                }
              >
                <Zap className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="truncate">
                  {t('authenticated.schedule.details.tabs.automations')}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="analytics"
                className="px-3 sm:px-6 flex items-center group min-w-0 flex-1 sm:flex-none"
                onClick={() =>
                  navigate({
                    to: '/schedule/$scheduleId/analytics',
                    params: { scheduleId },
                  })
                }
              >
                <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="truncate">
                  {t('authenticated.schedule.details.tabs.analytics')}
                </span>
              </TabsTrigger>
            </TabsList>

            <Outlet />
          </Tabs>
        </PageContent>
      )}

      {!activeTab && (
        <PageContent>
          <Outlet />
        </PageContent>
      )}
    </Page>
  );
}
